import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import {
  AlgComponentType,
  PreferenceLabel,
  PreferenceSlot,
  PrefLabelId,
  PrefSlotId,
  SectionSlot,
  User,
  UserPreference,
  UserPreferenceId,
  UserPreferenceType,
} from '@youshift/shared/types';
import { dateToString } from '@youshift/shared/utils';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

import ImportanceInput from './ImportanceInput';

interface UserBlocksPriorityByTypeProps {
  users: Record<number, User>;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
  userPreferences: Record<UserPreferenceId, UserPreference>;
  prefLabels: Record<PrefLabelId, PreferenceLabel>;
  preferenceSlots: Record<PrefSlotId, PreferenceSlot>;
  sectionSlots: Record<number, SectionSlot>;
  getTypeColor: (type: UserPreferenceType) => string;
}

type UserPreferenceComponentType =
  | AlgComponentType.C_PERSONAL_BLOCKING
  | AlgComponentType.C_JUSTIFIED_BLOCKING
  | AlgComponentType.C_SPECIAL_EVENTS;

const getAlgComponentType = (
  type: UserPreferenceType,
): UserPreferenceComponentType => {
  switch (type) {
    case UserPreferenceType.PERSONAL_BLOCKING:
      return AlgComponentType.C_PERSONAL_BLOCKING;
    case UserPreferenceType.JUSTIFIED_BLOCKING:
      return AlgComponentType.C_JUSTIFIED_BLOCKING;
    case UserPreferenceType.POINTS:
      return AlgComponentType.C_SPECIAL_EVENTS;
    default:
      return AlgComponentType.C_SPECIAL_EVENTS;
  }
};

const isUserPreferenceComponentType = (
  type: AlgComponentType,
): type is UserPreferenceComponentType => {
  return [
    AlgComponentType.C_PERSONAL_BLOCKING,
    AlgComponentType.C_JUSTIFIED_BLOCKING,
    AlgComponentType.C_SPECIAL_EVENTS,
  ].includes(type);
};

type SupportedPreferenceTypes =
  | UserPreferenceType.PERSONAL_BLOCKING
  | UserPreferenceType.JUSTIFIED_BLOCKING;

const isSupportedPreferenceType = (
  type: UserPreferenceType,
): type is SupportedPreferenceTypes => {
  return (
    type === UserPreferenceType.PERSONAL_BLOCKING ||
    type === UserPreferenceType.JUSTIFIED_BLOCKING
  );
};

interface TypeDisplayConfig {
  borderColor: string;
  backgroundColor: string;
  textColor: string;
  translationKey: string;
}

const TYPE_DISPLAY_CONFIG: Record<SupportedPreferenceTypes, TypeDisplayConfig> =
  {
    [UserPreferenceType.PERSONAL_BLOCKING]: {
      borderColor: '#EF4444', // red-500
      backgroundColor: '#FEF2F2', // red-50
      textColor: '#991B1B', // red-800
      translationKey: 'generic.personalBlock',
    },
    [UserPreferenceType.JUSTIFIED_BLOCKING]: {
      borderColor: '#F59E0B', // amber-500
      backgroundColor: '#FFFBEB', // amber-50
      textColor: '#92400E', // amber-800
      translationKey: 'generic.justifiedBlock',
    },
  };

export default function UserBlocksPriorityByType({
  users,
  importanceValues,
  setImportanceValues,
  userPreferences,
  prefLabels,
  preferenceSlots,
  sectionSlots,
  getTypeColor,
}: UserBlocksPriorityByTypeProps) {
  const { t } = useTranslation();
  const [expandedTypes, setExpandedTypes] = useState<
    Set<SupportedPreferenceTypes>
  >(new Set([]));

  const computeTypeModeImportance = useMemo(() => {
    const typeModes: Record<SupportedPreferenceTypes, number> = {} as Record<
      SupportedPreferenceTypes,
      number
    >;

    // Group preferences by type
    const prefsByType = Object.values(userPreferences).reduce(
      (acc, pref) => {
        if (!isSupportedPreferenceType(pref.preference)) {
          return acc;
        }
        if (!acc[pref.preference]) {
          acc[pref.preference] = [];
        }
        acc[pref.preference].push(pref);
        return acc;
      },
      {} as Record<SupportedPreferenceTypes, UserPreference[]>,
    );

    // Compute mode for each type
    Object.entries(prefsByType).forEach(([type, prefs]) => {
      const importanceCounts: Record<number, number> = {};
      let maxCount = 0;
      let mode = 0;

      prefs.forEach(pref => {
        const componentType = getAlgComponentType(type as UserPreferenceType);
        if (!isUserPreferenceComponentType(componentType)) return;

        const importance =
          importanceValues[componentType][pref.id_user_pref] ?? 0;
        importanceCounts[importance] = (importanceCounts[importance] || 0) + 1;

        if (importanceCounts[importance] > maxCount) {
          maxCount = importanceCounts[importance];
          mode = importance;
        }
      });

      typeModes[type as SupportedPreferenceTypes] = mode;
    });

    return typeModes;
  }, [userPreferences, importanceValues]);

  const toggleType = (type: SupportedPreferenceTypes) =>
    setExpandedTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });

  const renderBlockCard = (pref: UserPreference) => {
    const componentType =
      pref.preference === UserPreferenceType.PERSONAL_BLOCKING
        ? AlgComponentType.C_PERSONAL_BLOCKING
        : AlgComponentType.C_JUSTIFIED_BLOCKING;

    const importance = importanceValues[componentType][pref.id_user_pref] ?? 0;
    const slot = preferenceSlots[pref.id_pref_slot];
    const label = prefLabels[slot.id_pref_label];
    const user = users[pref.id_user];

    // Compute the earliest start and latest end times across all section slots
    const timeRange = slot.section_slots.reduce(
      (acc, slotId) => {
        const sectionSlot = sectionSlots[slotId];
        if (!sectionSlot) return acc;

        const start = new Date(sectionSlot.start).getTime();
        const end = new Date(sectionSlot.end).getTime();

        return {
          earliestStart: acc.earliestStart
            ? new Date(Math.min(acc.earliestStart.getTime(), start))
            : new Date(start),
          latestEnd: acc.latestEnd
            ? new Date(Math.max(acc.latestEnd.getTime(), end))
            : new Date(end),
        };
      },
      {
        earliestStart: null as Date | null,
        latestEnd: null as Date | null,
      },
    );

    return (
      <div
        key={pref.id_user_pref}
        className="grid grid-cols-[3fr,1fr,2fr] gap-x-4 items-center p-2 hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {user.firstname} {user.lastname}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {label && (
                <span className="text-sm font-medium">{label.name}</span>
              )}
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getTypeColor(pref.preference)}`}
              >
                {pref.preference === UserPreferenceType.PERSONAL_BLOCKING
                  ? t('generic.personalBlock')
                  : t('generic.justifiedBlock')}
              </span>
            </div>

            {timeRange.earliestStart && timeRange.latestEnd && (
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">
                  {dateToString(timeRange.earliestStart, 'weekday-hour')} -{' '}
                  {dateToString(timeRange.latestEnd, 'weekday-hour')}
                </span>
              </div>
            )}
            <div className="text-sm text-gray-500">
              {pref.justification && (
                <p className="italic">
                  {t('generic.justification')} {pref.justification}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {t('generic.priority')} {': '}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <ImportanceInput
              value={importance}
              onChange={value =>
                setImportanceValues(prev => ({
                  ...prev,
                  [componentType]: {
                    ...prev[componentType],
                    [pref.id_user_pref]: value,
                  },
                }))
              }
            />
          </div>
        </div>
      </div>
    );
  };

  // Group preferences by type
  const preferencesByType = Object.values(userPreferences).reduce(
    (acc, pref) => {
      if (
        pref.preference !== UserPreferenceType.PERSONAL_BLOCKING &&
        pref.preference !== UserPreferenceType.JUSTIFIED_BLOCKING
      ) {
        return acc;
      }
      if (!acc[pref.preference]) {
        acc[pref.preference] = [];
      }
      acc[pref.preference].push(pref);
      return acc;
    },
    {} as Record<SupportedPreferenceTypes, UserPreference[]>,
  );

  // Create a new object with sorted preferences by type
  const sortedPreferencesByType = Object.entries(preferencesByType).reduce(
    (acc, [type, prefs]) => {
      acc[type as SupportedPreferenceTypes] = [...prefs].sort((a, b) => {
        const userA = users[a.id_user];
        const userB = users[b.id_user];
        return userA.firstname.localeCompare(userB.firstname);
      });
      return acc;
    },
    {} as Record<
      | UserPreferenceType.PERSONAL_BLOCKING
      | UserPreferenceType.JUSTIFIED_BLOCKING,
      UserPreference[]
    >,
  );

  return (
    <div className="space-y-4">
      {Object.entries(sortedPreferencesByType).map(([type, preferences]) => {
        const isExpanded = expandedTypes.has(type as SupportedPreferenceTypes);
        const typeModeImportance =
          computeTypeModeImportance[type as SupportedPreferenceTypes];
        const componentType = getAlgComponentType(type as UserPreferenceType);
        const displayConfig =
          TYPE_DISPLAY_CONFIG[type as SupportedPreferenceTypes];

        return (
          <div key={type} className="border rounded">
            <button
              onClick={() => toggleType(type as SupportedPreferenceTypes)}
              className="flex items-center justify-between w-full text-left p-2"
              style={{
                borderLeft: `4px solid ${displayConfig.borderColor}`,
                backgroundColor: displayConfig.backgroundColor,
                color: displayConfig.textColor,
              }}
            >
              <div className="flex items-center gap-2">
                <div className="font-medium">
                  {t(displayConfig.translationKey)}
                </div>
                <span className="text-sm text-gray-500">
                  ({preferences.length})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ImportanceInput
                  value={typeModeImportance}
                  onChange={value => {
                    const updatedValues: ComponentPrioritySchema = {
                      ...importanceValues,
                      [componentType]: {
                        ...importanceValues[componentType],
                        ...preferences.reduce(
                          (acc, pref) => ({
                            ...acc,
                            [pref.id_user_pref]: value,
                          }),
                          {},
                        ),
                      },
                    };
                    setImportanceValues(() => updatedValues);
                  }}
                />
                {isExpanded ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </div>
            </button>
            {isExpanded && (
              <div className="p-2 space-y-2">
                {preferences.map(renderBlockCard)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
